import { ApprovalModel, Approver, PaginatedResource } from "~/application/types";
import { api } from "~/infrastructure/api";
import { ApprovalModelDTO, ApproverDTO, UpdateApprovalModelDTO } from "~/infrastructure/api/dtos";
import { mapApprovalModelDTO, mapApproverDTO } from "~/infrastructure/api/mappers";
import {
  IAddApproversParams,
  IApprovalModelService,
  ICreateApprovalModelParams,
  IFindApprovalModelParams,
  IFindApproverModelApproversParams,
  IFindByApprovalModelParams,
  ILinkingNewCostCenterData,
} from "./IApprovalModelService";
import queryString from "query-string";

export class ApprovalModelService implements IApprovalModelService {
  async find({
    name,
    page,
    active,
    customerId,
    approvers_uuid,
  }: IFindApprovalModelParams): Promise<PaginatedResource<ApprovalModel[]>> {
    const url = queryString.stringifyUrl(
      {
        url: `customers/${customerId}/approval-models`,
        query: {
          page,
          name,
          active,
          approvers_uuid,
        },
      },
      {
        arrayFormat: "bracket",
      },
    );

    return await api
      .get<PaginatedResource<ApprovalModelDTO[]>>(url)
      .then(({ data: { data, links, meta } }) => ({
        data: data.map(mapApprovalModelDTO),
        links,
        meta,
      }));
  }

  async findByApprovalModel({
    costCenterId,
    phaseId,
    projectId,
    minimal,
  }: IFindByApprovalModelParams): Promise<ApprovalModel[]> {
    const url = queryString.stringifyUrl(
      {
        url: `/approval-models`,
        query: {
          cost_center_uuid: costCenterId,
          phase_uuid: phaseId,
          project_uuid: projectId,
          minimal,
        },
      },
      {
        arrayFormat: "bracket",
      },
    );
    return await api
      .get<{ data: ApprovalModelDTO[] }>(url)
      .then(({ data: { data } }) => data.map(mapApprovalModelDTO));
  }

  async findApprovers({
    customerId,
    costCenterId,
    projectId,
    phaseId,
    groupId,
    branchId,
    modelType,
    name,
  }: IFindApproverModelApproversParams): Promise<Approver[]> {
    const url = queryString.stringifyUrl(
      {
        url: `/customers/${customerId}/approval-models/approvers`,
        query: {
          cost_center_uuid: costCenterId,
          project_uuid: projectId,
          phase_uuid: phaseId,
          group_uuid: groupId,
          branch_uuid: branchId,
          model_type: modelType,
          name,
        },
      },
      {
        skipNull: true,
        skipEmptyString: true,
      },
    );

    return await api.get(url).then(({ data }) =>
      data.data.map((approver: ApproverDTO) => ({
        uuid: approver.uuid,
        level: approver.level,
        selfApprover: approver.self_approver,
        name: approver.name,
      })),
    );
  }

  async findById(id: string): Promise<ApprovalModel> {
    return await api
      .get<{ data: ApprovalModelDTO }>(`/customers/approval-models/${id}`)
      .then(({ data }) => mapApprovalModelDTO(data.data));
  }

  async create({ customerId, data }: ICreateApprovalModelParams): Promise<ApprovalModel> {
    const url = `/customers/${customerId}/approval-models`;
    return await api
      .post<PaginatedResource<ApprovalModelDTO>>(url, data)
      .then(({ data: response }) => mapApprovalModelDTO(response.data));
  }

  async update(data: UpdateApprovalModelDTO): Promise<ApprovalModel> {
    return await api
      .put<ApprovalModelDTO>(`customers/approval-models/${data.uuid}`, data)
      .then(({ data }) => mapApprovalModelDTO(data));
  }

  async getAllLikedApprovallModels(approvalModelId: string) {
    return await api
      .get<{
        data: { uuid: string; name: string }[];
      }>(`/customers/approval-models/${approvalModelId}/linked-models`)
      .then(({ data }) => data.data);
  }

  async addApprover({
    uuid,
    selfApprover,
    level,
    approvalModelId,
  }: Approver & { approvalModelId: string }): Promise<Approver> {
    const url = `/customers/approval-models/${approvalModelId}/approvers`;
    const body = {
      approval_model_uuid: approvalModelId,
      self_approver: !!selfApprover,
      approver_uuid: uuid,
      level: level,
    };

    return api.post<ApproverDTO>(url, body).then(({ data }) => mapApproverDTO(data));
  }

  async linkingNewCostCenter({
    costCenter,
    approvalModelId,
  }: ILinkingNewCostCenterData): Promise<void> {
    const body = {
      cost_center_uuid: costCenter.uuid,
    };

    await api.patch<void>(`customers/approval-models/${approvalModelId}/cost-centers`, body);
  }

  async addApprovers({ approvers, approvalModelId }: IAddApproversParams): Promise<Approver[]> {
    return await Promise.all(
      approvers.map(async (approver) => {
        return this.addApprover({ ...approver, approvalModelId });
      }),
    );
  }

  async updateApprover({
    uuid,
    selfApprover,
    level,
    approvalModelId,
  }: Approver & { approvalModelId: string }): Promise<Approver> {
    const url = `/customers/approval-models/${approvalModelId}/approvers/${uuid}`;
    return api
      .put<ApproverDTO>(url, { self_approver: selfApprover, level })
      .then(({ data }) => mapApproverDTO(data));
  }

  async updateApprovers(
    approvers: (Approver & { approvalModelId: string })[],
  ): Promise<Approver[]> {
    return await Promise.all(approvers.map(async (approver) => this.updateApprover(approver)));
  }

  async inactivateApprover(approverModelId: string): Promise<void> {
    const url = `/customers/approval-models/approver-models/${approverModelId}/inactive`;
    return api.patch(url).then(({ data }) => data);
  }

  async toggleActive(data: ApprovalModel): Promise<void> {
    return data.isActive ? this.inactivate(data) : this.activate(data);
  }

  private async activate({ uuid }: ApprovalModel): Promise<void> {
    await api.patch<void>(`/customers/approval-models/${uuid}/active`);
  }

  private async inactivate({ uuid }: ApprovalModel): Promise<void> {
    await api.patch<void>(`/customers/approval-models/${uuid}/inactive`);
  }
}
